import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { isFeatureEnabled } from '@/modules/common/hooks/use-feature-flags';
import {
  invalidateSuggestedApproversQueries,
  invalidateTimeTrackerProjectsQueries,
  invalidateTimeTrackingQueries,
} from '@/modules/common/utils/query-invalidation';

import { deleteAssignment } from '../../api/assignments.api';
import {
  applyOptimisticMemberUpdate,
  cancelAssignmentQueries,
  findAssignmentInCache,
  invalidateAllAssignmentQueries,
  OptimisticContext,
  patchCacheAfterMutation,
  refetchActiveAssignmentQueriesInBackground,
  rollbackAssignmentData,
  snapshotAssignmentData,
} from '../../helpers/assignment-cache-utils';
import { DeleteAssignmentWithUIRequest, PlannerMemberResponse } from '../../types/dto';

/**
 * Hook for deleting an assignment with optimistic updates.
 * Immediately removes the assignment from the UI before the API call completes.
 * Automatically invalidates only the visible date range queries on success.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, etc.
 *
 * @example
 * ```typescript
 * const deleteMutation = useDeleteAssignment({
 *   onSuccess: () => logger.debug(TAG, 'Assignment deleted!')
 * });
 *
 * deleteMutation.mutate({
 *   assignmentId: 123
 * });
 * ```
 */
export function useDeleteAssignment(
  options?: UseMutationOptions<void, Error, DeleteAssignmentWithUIRequest>
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.planner');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    ...restOptions,
    mutationFn: (request: DeleteAssignmentWithUIRequest) => deleteAssignment(request.payload),
    onMutate: async (variables: DeleteAssignmentWithUIRequest): Promise<OptimisticContext> => {
      const { payload } = variables;

      // Cancel outgoing refetches to prevent overwriting optimistic update
      await cancelAssignmentQueries(queryClient);

      // Snapshot previous values for rollback
      const previousData = snapshotAssignmentData(queryClient);

      // Find the assignment's current dates and owner for utilization recalculation
      const cachedAssignment = findAssignmentInCache(queryClient, payload.assignmentId);
      const oldAssignmentDates = cachedAssignment?.dates;
      const assignmentUserId = cachedAssignment?.userId;

      // Helper to remove assignment from a member
      const removeAssignmentFromMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
        return {
          ...member,
          assignments: member.assignments.filter(
            plannerAssignment => plannerAssignment.assignment.id !== payload.assignmentId
          ),
        };
      };

      // Optimistically update all three cache views (byMember, byDepartment, byProject)
      applyOptimisticMemberUpdate(queryClient, removeAssignmentFromMember);

      return { ...previousData, oldAssignmentDates, assignmentUserId };
    },
    onSuccess: (data, variables, context) => {
      if (isFeatureEnabled('usePlannerCachePatching')) {
        const ctx = context as OptimisticContext;

        // Recalculate utilization for the deleted assignment's date range
        if (ctx.assignmentUserId && ctx.oldAssignmentDates) {
          patchCacheAfterMutation(queryClient, {
            userId: ctx.assignmentUserId,
            oldDates: ctx.oldAssignmentDates,
          });
        }

        // Fire-and-forget background refetch to reconcile activePeriods from backend
        // (client-side cannot compute rate-change-based period splits).
        refetchActiveAssignmentQueriesInBackground(queryClient);
      } else {
        invalidateAllAssignmentQueries(queryClient);
      }

      // Assignment deletion affects booked hours shown on the time tracking page
      invalidateTimeTrackingQueries(queryClient);
      // Assignment changes can alter the time-off suggested approvers.
      invalidateSuggestedApproversQueries(queryClient);
      // Assignment changes can flip which projects count as "booked" in the
      // Time Tracking modal's project picker.
      invalidateTimeTrackerProjectsQueries(queryClient);

      displayToastSuccess(
        t('toasts.assignmentDeletedSuccess', {
          userName: variables.ui?.userName ?? '',
          projectName: variables.ui?.projectName ?? '',
        })
      );

      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      rollbackAssignmentData(queryClient, context as OptimisticContext);

      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.planner:toasts.deleteAssignmentError',
        {
          interpolation: {
            userName: variables.ui?.userName ?? '',
            projectName: variables.ui?.projectName ?? '',
          },
        }
      );

      onError?.(error, variables, context);
    },
  });
}
